import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { teamsAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import { FaFutbol, FaMapMarkerAlt, FaBuilding, FaArrowLeft, FaUsers, FaSearch } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import PageCover from '../components/PageCover';
import { useLanguage } from '../context/LanguageContext';

const GROUP_OPTIONS = [1, 2, 3, 4, 5, 6];

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { language } = useLanguage();

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await teamsAPI.getAll();
      const raw = response?.data;
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.teams) ? raw.teams : []);
      setTeams(list);
    } catch (error) {
      console.error('Erreur:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const teamsByGroup = useMemo(() => {
    return teams.reduce((acc, team) => {
      const g = team.group_number;
      const group = g !== undefined && g !== null && g !== '' ? Number(g) : 1;
      if (!acc[group]) acc[group] = [];
      acc[group].push(team);
      return acc;
    }, {});
  }, [teams]);

  const filterTeam = (team) => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return true;
    const name = (team.name || '').toLowerCase();
    const city = (team.city || '').toLowerCase();
    const stadium = (team.stadium || '').toLowerCase();
    return name.includes(q) || city.includes(q) || stadium.includes(q);
  };

  const groupsToShow = useMemo(() => {
    if (selectedGroup === 'all') return GROUP_OPTIONS;
    const g = Number(selectedGroup);
    return Number.isInteger(g) && g >= 1 && g <= 6 ? [g] : GROUP_OPTIONS;
  }, [selectedGroup]);

  const t = {
    title: language === 'ar' ? 'الفرق المشاركة' : 'Équipes participantes',
    subtitle: language === 'ar' ? 'جميع الفرق المشاركة في الرابطة الوطنية - المستوى الثاني' : 'Toutes les équipes de la Ligue Amateur Niveau 2',
    team: language === 'ar' ? 'فريق' : 'équipe',
    teams: language === 'ar' ? 'فرق' : 'équipes',
    group: language === 'ar' ? 'المجموعة' : 'Groupe',
    filterByGroup: language === 'ar' ? 'تصفية حسب المجموعة' : 'Filtrer par groupe',
    allGroups: language === 'ar' ? 'جميع المجموعات' : 'Toutes les groupes',
    searchPlaceholder: language === 'ar' ? 'بحث بالاسم أو المدينة...' : 'Rechercher par nom ou ville...',
    viewDetails: language === 'ar' ? 'عرض التفاصيل' : 'Voir détails',
    notSpecified: language === 'ar' ? 'غير محدد' : 'Non spécifié'
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.container}>
      <PageCover title={t.title} subtitle={t.subtitle} icon={FaFutbol} />

      {/* Stats bar */}
      <section style={styles.statsSection}>
        <div style={styles.statsBar}>
          <div style={styles.stat}>
            <span style={styles.statNumber}>{teams.length}</span>
            <span style={styles.statLabel}>{t.teams}</span>
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section style={styles.filtersSection}>
        <div style={styles.filtersBar}>
          <div style={styles.filterGroup}>
            <FaUsers style={styles.filterIcon} />
            <span style={styles.filterLabel}>{t.filterByGroup}</span>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              style={styles.select}
              aria-label={t.filterByGroup}
            >
              <option value="all">{t.allGroups}</option>
              {GROUP_OPTIONS.map((g) => (
                <option key={g} value={g}>{t.group} {g}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <FaSearch style={styles.filterIcon} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              style={styles.searchInput}
              aria-label={t.searchPlaceholder}
            />
          </div>
        </div>
      </section>

      {/* Affichage par groupe */}
      <section style={styles.teamsSection}>
        {groupsToShow.map((groupNum) => {
          const groupTeams = (teamsByGroup[groupNum] || []).filter(filterTeam);
          if (groupTeams.length === 0) return null;
          return (
            <div key={groupNum} style={styles.groupBlock}>
              <div style={styles.groupHeader}>
                <FaUsers style={styles.groupIcon} />
                <h2 style={styles.groupTitle}>{t.group} {groupNum}</h2>
                <span style={styles.groupCount}>({groupTeams.length} {t.teams})</span>
              </div>
              <div style={styles.teamsGrid}>
                {groupTeams.map((team, index) => (
                  <Link
                    key={team.id}
                    to={`/teams/${team.id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{ ...styles.teamCard, animationDelay: `${index * 0.05}s` }}>
                      <div style={styles.teamHeader}>
                        {getLogoUrl(team.logo_url) ? (
                          <img
                            src={getLogoUrl(team.logo_url)}
                            alt={team.name}
                            style={styles.teamLogo}
                            onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div style={{ ...styles.logoPlaceholder, display: getLogoUrl(team.logo_url) ? 'none' : 'flex' }}>
                          <FaFutbol size={50} color="#c41e3a" />
                        </div>
                      </div>
                      <div style={styles.teamBody}>
                        <h3 style={styles.teamName}>{team.name}</h3>
                        <div style={styles.teamInfo}>
                          <div style={styles.infoItem}>
                            <FaMapMarkerAlt style={styles.infoIcon} />
                            <span>{team.city || t.notSpecified}</span>
                          </div>
                          {team.stadium && (
                            <div style={styles.infoItem}>
                              <FaBuilding style={styles.infoIcon} />
                              <span>{team.stadium}</span>
                            </div>
                          )}
                        </div>
                        <div style={styles.viewDetails}>
                          <FaArrowLeft style={styles.arrowIcon} />
                          <span>{t.viewDetails}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
        {groupsToShow.every((g) => !(teamsByGroup[g] || []).filter(filterTeam).length) && (
          <p style={styles.noResults}>
            {language === 'ar' ? 'لا توجد فرق تطابق البحث.' : 'Aucune équipe ne correspond au filtre.'}
          </p>
        )}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    width: '100%',
    maxWidth: '100vw',
    overflowX: 'hidden'
  },
  statsSection: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px 16px'
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginTop: '30px'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'white',
    padding: '20px 40px',
    borderRadius: '15px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid #e8ecf0'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#1a365d'
  },
  statLabel: {
    fontSize: '16px',
    marginTop: '5px'
  },
  filtersSection: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px 20px 0'
  },
  filtersBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    alignItems: 'center',
    background: 'white',
    padding: '16px 24px',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },
  filterIcon: {
    color: '#1a365d',
    fontSize: '18px'
  },
  filterLabel: {
    fontSize: '14px',
    color: '#555',
    fontWeight: 600
  },
  select: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    minWidth: '160px',
    background: 'white',
    cursor: 'pointer'
  },
  searchInput: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    minWidth: '220px'
  },
  teamsSection: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '40px 20px 60px'
  },
  groupBlock: {
    marginBottom: '48px'
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    paddingBottom: '12px',
    borderBottom: '3px solid #1a365d'
  },
  groupIcon: {
    fontSize: '28px',
    color: '#1a365d'
  },
  groupTitle: {
    fontSize: '26px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0
  },
  groupCount: {
    fontSize: '16px',
    color: '#666',
    marginLeft: 'auto'
  },
  noResults: {
    textAlign: 'center',
    color: '#666',
    fontSize: '16px',
    padding: '40px 20px'
  },
  teamsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '30px'
  },
  teamCard: {
    background: 'white',
    borderRadius: '15px',
    overflow: 'hidden',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    transition: 'all 0.3s',
    cursor: 'pointer',
    animation: 'fadeInUp 0.6s ease forwards',
    opacity: 0
  },
  teamHeader: {
    background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px'
  },
  teamLogo: {
    maxWidth: '120px',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
  },
  logoPlaceholder: {
    width: '120px',
    height: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    borderRadius: '50%',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
  },
  teamBody: {
    padding: '25px'
  },
  teamName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '20px',
    textAlign: 'center',
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  teamInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#666',
    fontSize: '14px'
  },
  infoIcon: {
    color: '#1a365d',
    fontSize: '16px'
  },
  viewDetails: {
    marginTop: '20px',
    padding: '12px',
    background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'all 0.3s'
  },
  arrowIcon: {
    fontSize: '14px'
  }
};

export default TeamsPage;
